// src/pages/student/StudentSessions.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchStudentSessions } from '../../api/studentPortal';
import { usePortalStudentId } from '../../hooks/usePortalStudentId';
import ParentPortalHeader from '../../components/parent/ParentPortalHeader';
import SessionJoinAction from '../../components/classroom/SessionJoinAction';
import { formatDateTime } from "../../utils/date";
import UserLink from '../../components/common/UserLink';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import PaginationFooter from '../../components/common/PaginationFooter';
import useClientPagination from '../../hooks/useClientPagination';
import { localizeBatchName } from '../../utils/displayNames';


function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const StudentSessions = () => {
    const { t } = useTranslation();
    const childStudentId = usePortalStudentId();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            setLoading(true); setErr('');
            try {
                const data = await fetchStudentSessions(childStudentId, { signal: ac.signal });
                const rows = Array.isArray(data) ? data : [];
                setRows(rows);
            } catch (e) {
                setErr(t('pages.studentSessions.loadFailed'));
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [childStudentId, t]);

    // Keep Join flags fresh when coach starts/ends without forcing a full page refresh.
    useEffect(() => {
        if (loading || err) return undefined;
        const timer = window.setInterval(() => {
            fetchStudentSessions(childStudentId)
                .then((data) => {
                    if (Array.isArray(data)) setRows(data);
                })
                .catch(() => { /* keep last good snapshot */ });
        }, 15000);
        return () => window.clearInterval(timer);
    }, [childStudentId, loading, err]);

    const filtered = useMemo(() => {
        const term = (dq || '').trim().toLowerCase();
        if (!term) return rows || [];
        const pick = (x) => (x ?? '').toString().toLowerCase();
        return (rows || []).filter(r =>
            [
                r.sessionId, r.levelName, r.batchName,
                r.coachName, r.coachId,
                r.status, r.attendanceType,
                r.startTime, r.endTime
            ].some(v => pick(v).includes(term))
        );
    }, [rows, dq]);

    const pagination = useClientPagination(filtered, dq);

    const fmtDT = (v) => {
        if (!v) return '-';
        try {
            const d = new Date(v);
            if (isNaN(d.getTime())) return '-';
            return formatDateTime(d);
        } catch { return '-'; }
    };

    const badge = (val) => {
        const base = "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium";
        const map = {
            UPCOMING: "bg-blue-100 text-blue-700",
            ONGOING: "bg-green-100 text-green-700",
            PAST: "bg-gray-100 text-gray-700",
            PRESENT: "bg-green-100 text-green-700",
            ABSENT: "bg-red-100 text-red-700",
        };
        const label = val
            ? t(`reports.common.${String(val).toLowerCase()}`, { defaultValue: val })
            : '—';
        return <span className={`${base} ${map[val] || "bg-gray-100 text-gray-700"}`}>{label}</span>;
    };

    const coachCell = (r) => {
        const name = r.coachName && String(r.coachName).trim();
        const id = r.coachId;
        if (name && id) return <UserLink id={id} name={name} />;
        if (name) return name;
        if (id) return `#${id}`;
        return '-';
    };

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell"
                title={t('pages.studentSessions.title')}
                subtitle={t('pages.studentSessions.subtitle')}
                showBack={false}
            />
        );
    }
    if (err) return <div className="p-6 text-red-600">{err}</div>;

    const shown = filtered.length;
    const total = rows.length;

    return (
        <div className="list-page">
            <ParentPortalHeader />
            <PageHeaderSection
                title={t('pages.studentSessions.title')}
                subtitle={t('pages.studentSessions.subtitle')}
            />

            <div className="rounded-lg border bg-white p-4">
                <div className="relative">
                    <svg
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                        viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M12.9 14.32a8 8 0 111.414-1.414l3.387 3.387a1 1 0 01-1.414 1.414l-3.387-3.387zM14 8a6 6 0 11-12 0 6 6 0 0112 0z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        type="text"
                        placeholder={t('pages.studentSessions.searchPlaceholder')}
                        className="w-full border rounded pl-10 pr-3 py-2 outline-none focus:ring"
                    />
                </div>
                <div className="mt-1 text-xs text-gray-500">
                    {t('common.showing')} {shown} {t('common.of')} {total}
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="text-gray-600">{t('pages.studentSessions.empty')}</div>
            ) : (
                <div className="bg-white rounded shadow overflow-x-auto">
                    <table className="min-w-full text-sm text-center">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-center">{t('table.start')}</th>
                                <th className="px-4 py-2 text-center">{t('table.end')}</th>
                                <th className="px-4 py-2 text-center">{t('table.level')}</th>
                                <th className="px-4 py-2 text-center">{t('table.batch')}</th>
                                <th className="px-4 py-2 text-center">{t('table.coach')}</th>
                                <th className="px-4 py-2 text-center">{t('common.status')}</th>
                                <th className="px-4 py-2 text-center">{t('table.attendance')}</th>
                                <th className="px-4 py-2 text-center">{t('classroom.live.joinSession', { defaultValue: 'Join Session' })}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagination.pageItems.map((r, idx) => (
                                <tr key={String(r.sessionId ?? idx)} className="border-t hover:bg-gray-50">
                                    <td className="px-4 py-2 text-center">{fmtDT(r.startTime)}</td>
                                    <td className="px-4 py-2 text-center">{fmtDT(r.endTime)}</td>
                                    <td className="px-4 py-2 text-center">{r.levelName || '-'}</td>
                                    <td className="px-4 py-2 text-center">{localizeBatchName(r.batchName, t) || '-'}</td>
                                    <td className="px-4 py-2 text-center">{coachCell(r)}</td>
                                    <td className="px-4 py-2 text-center">{badge(r.status)}</td>
                                    <td className="px-4 py-2 text-center">{badge(r.attendanceType)}</td>
                                    <td className="px-4 py-2 text-center">
                                        <SessionJoinAction
                                            sessionId={r.sessionId}
                                            role={childStudentId ? 'PARENT' : 'STUDENT'}
                                            classroomEligible={r.classroomEligible}
                                            canJoin={r.canJoin}
                                            meetingStatus={r.meetingStatus}
                                            status={r.status}
                                            endTime={r.endTime}
                                            studentId={childStudentId}
                                            compact
                                        />
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td className="px-4 py-6 text-center text-gray-500" colSpan={8}>
                                        {t('pages.studentSessions.noMatch')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default StudentSessions;
